import { Button, TableItem, TableMessage } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useNewWindowNavigate } from 'hooks/useNewWindowNavigate';
import { useCustomParamId, useParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useStoreState } from 'providers/StoreStateContext';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCalendar,
  PiCheck,
  PiClockCounterClockwise,
  PiNavigationArrow,
  PiPencil,
  PiPlus,
  PiTrash,
  PiX,
} from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import {
  IChannelVideoForm,
  IContractResponse,
} from 'services/channel/types/query';
import { useGetContractsByLabel } from 'services/contract';
import { PRODUCT_TYPE } from 'services/contract/types';
import { useGetLabelLightList } from 'services/labels/light';
import { ISettlementInfoContractForm } from 'services/music-product-digital/types/form';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDate, formatDateToInput } from 'utils/global/date.utils';
import { getNodes } from 'utils/table/nodes.utils';

interface IChannelVideoContractSeletcProps extends ISettlementInfoContractForm {
  id: number;
}

const VideoContractSeletc = ({
  id,
  contractId,
  contractName,
  labelId,
}: IChannelVideoContractSeletcProps) => {
  const { values, setFieldValue }: IFormikContextType<IChannelVideoForm> =
    useFormikContext<IChannelVideoForm>();
  const { t } = useTranslation();

  const labelContracts = useGetContractsByLabel(labelId, {
    params: { itemCount: 999, page: 0, ProductType: PRODUCT_TYPE.YouTube },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const contractOptions =
    labelContracts.data?.pages
      .flat()
      .filter(
        labelContract =>
          !values.videoContracts
            .map(c => c.contractId)
            .includes(labelContract.id)
      )
      .map(contract => ({ value: contract.id, label: contract.title })) ?? [];

  useEffect(() => {
    labelContracts.refetch();
  }, [labelId]);

  return (
    <FormInput.Select
      name={`videoContracts[${id}].contractId`}
      isAsync
      isSearchable
      label={t(`input.label.contract`)}
      onChange={value => {
        setFieldValue(`videoContracts[${id}].contractName`, value.label);
      }}
      value={{
        label: contractName,
        value: contractId,
      }}
      options={contractOptions}
      isLoading={labelContracts.isLoading}
    />
  );
};

const ChannelVideoContracts = () => {
  const channelId = useParamId('channel');
  const videoId = useCustomParamId('videoId');

  usePermissionGuard('ContractPermissionsLIST**');

  const navigateToNewWindow = useNewWindowNavigate();

  const navigate = useNavigate();

  const {
    values,
    setFieldValue,
    status,
  }: IFormikContextType<IChannelVideoForm> =
    useFormikContext<IChannelVideoForm>();

  const { t } = useTranslation();

  const i18nextPrefix = 'channel.contract';

  const tableDate = getNodes<IContractResponse>([]);

  const labels = useGetLabelLightList();

  const { applicationSettings } = useStoreState();

  const getLabelOptions = (contracts: ISettlementInfoContractForm[]) =>
    labels.data
      ?.filter(label =>
        applicationSettings?.allowContractsWithSameLabel
          ? true
          : !contracts.map(contract => contract.labelId).includes(label.id)
      )
      .map(label => ({ value: label.id, label: label.labelName })) ?? [];

  const getDivisionPercentageSum = (contracts: ISettlementInfoContractForm[]) =>
    contracts
      .map(contract => contract.divisionPercentage ?? 0)
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0) ??
    0;

  const getContractsWithoutDivision = (
    contracts: ISettlementInfoContractForm[]
  ) =>
    contracts.filter(
      ({ divisionPercentage }) =>
        divisionPercentage === null || divisionPercentage === undefined
    ).length ?? 1;

  const getPercentageLeft = (contracts: ISettlementInfoContractForm[]) =>
    getDivisionPercentageSum(contracts) < 100
      ? parseFloat(
          (
            (100 - getDivisionPercentageSum(contracts)) /
            getContractsWithoutDivision(contracts)
          ).toFixed(2)
        )
      : 0;

  const onAddContract = () => {
    setFieldValue(`videoContracts[${values.videoContracts?.length}]`, {
      contractId: '',
      contractName: '',
      labelId: '',
      labelName: '',
      startDate: formatDateToInput(new Date()),
      endDate: null,
      divisionPercentage: null,
    });
  };

  const onRemoveContract = (index: number) => {
    setFieldValue(
      'videoContracts',
      values.videoContracts.filter((_, key) => key !== index)
    );
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          hideHeader
          topbarContent={
            <div className='flex w-full justify-end'>
              <div className='flex gap-2'>
                <Button onlyIcon isClear onClick={() => navigate('future')}>
                  <Button.Icon icon={<PiCalendar />} />
                </Button>
                <Button onlyIcon isClear onClick={() => navigate('history')}>
                  <Button.Icon icon={<PiClockCounterClockwise />} />
                </Button>
                <div />
                {status?.displayMode ? (
                  <Button
                    onlyIcon
                    isClear
                    onClick={() =>
                      navigate(
                        `/channel/${channelId}/channel-video/edit/${videoId}/contracts`
                      )
                    }>
                    <Button.Icon icon={<PiPencil />} />
                  </Button>
                ) : (
                  <>
                    <Button
                      isClear
                      onlyIcon
                      onClick={() =>
                        navigate(
                          `/channel/${channelId}/channel-video/${videoId}/contracts`
                        )
                      }>
                      <Button.Icon icon={<PiX />} />
                    </Button>
                    <Button variant={BUTTON_VARIANT.submit} type='submit'>
                      <Button.Text text={t('button.save')} />
                    </Button>
                  </>
                )}
              </div>
            </div>
          }
        />
      }>
      <Table
        id={'VideoContractsForm'}
        size='compact'
        data={{
          nodes: values.videoContracts?.map((_, i) => ({ ..._, id: i })) ?? [],
        }}
        columns={[
          {
            label: `${t('input.label.label')}`,
            renderCell: ({ labelName: name, labelId: id, id: index }) => (
              <TableItem>
                {status?.displayMode ? (
                  <TableItem.Text text={name} />
                ) : (
                  <FormInput.Select
                    name={`videoContracts[${index}].labelId`}
                    isAsync
                    isSearchable
                    label={t(`input.label.label`)}
                    onChange={value => {
                      setFieldValue(
                        `videoContracts[${index}].labelName`,
                        value.label
                      );
                      setFieldValue(
                        `videoContracts[${index}].contractName`,
                        ' '
                      );
                      setFieldValue(`videoContracts[${index}].contractId`, ' ');
                    }}
                    value={{
                      label: name,
                      value: id,
                    }}
                    options={getLabelOptions(values.videoContracts ?? [])}
                    isLoading={labels.isLoading}
                  />
                )}
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t(`input.label.contract`),
            renderCell: contract => (
              <TableItem>
                {status?.displayMode ? (
                  <TableItem.Text text={contract.contractName} />
                ) : !contract.labelId.length ? (
                  <FormInput.Select
                    name={`videoContracts[${contract.id}].contractId`}
                    label={t(`input.label.contract`)}
                    isDisabled
                  />
                ) : (
                  <VideoContractSeletc {...contract} />
                )}
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t(`input.label.date`),
            renderCell: contract => (
              <TableItem>
                {status?.displayMode ? (
                  <TableItem.Text text={`${formatDate(contract.startDate)}`} />
                ) : (
                  <FormInput.Text
                    name={`videoContracts[${contract.id}].startDate`}
                    type='date'
                    label={t('input.label.startDate')}
                  />
                )}
              </TableItem>
            ),
            resize: true,
          },

          {
            label: t(`input.label.division`),
            renderCell: ({ divisionPercentage, id: index }) => (
              <TableItem>
                {status?.displayMode ? (
                  <TableItem.Text text={`${divisionPercentage ?? '-'}%`} />
                ) : (
                  <div className='flex w-full gap-2'>
                    <div>
                      <FormInput.Number
                        name={`videoContracts[${index}].divisionPercentage`}
                        label={t(`input.label.division`)}
                        suffix=' %'
                        placeholder={`${getPercentageLeft(
                          values.videoContracts ?? []
                        )} %`}
                      />
                    </div>
                    {values.videoContracts[index].divisionPercentage ===
                      null && (
                      <Button
                        size='compact'
                        isClear
                        onlyIcon
                        onClick={() =>
                          setFieldValue(
                            `videoContracts[${index}].divisionPercentage`,
                            getPercentageLeft(values.videoContracts ?? [])
                          )
                        }>
                        <Button.Icon icon={<PiCheck />} />
                      </Button>
                    )}
                  </div>
                )}
              </TableItem>
            ),
          },
          {
            label: '',
            renderCell: ({ id: index, labelId, contractId }) => (
              <TableItem justifyPosition='end'>
                {status?.displayMode ? (
                  <Button
                    isClear
                    onlyIcon
                    onClick={() =>
                      navigateToNewWindow(
                        `label/${labelId}/contract/${contractId}/form`
                      )
                    }>
                    <Button.Icon
                      icon={<PiNavigationArrow transform='scale(-1,1)' />}
                    />
                  </Button>
                ) : (
                  <Button
                    variant={BUTTON_VARIANT.remove}
                    isClear
                    onlyIcon
                    onClick={() => onRemoveContract(index)}>
                    <Button.Icon icon={<PiTrash />} />
                  </Button>
                )}
              </TableItem>
            ),
          },
        ]}
        tableOptions={
          status?.displayMode
            ? undefined
            : {
                renderAfterTable: () => (
                  <tfoot className='full-width-footer'>
                    <TableMessage
                      className='cursor-pointer'
                      onClick={onAddContract}>
                      <TableItem.Text text={t('button.add')} />
                      <PiPlus />
                    </TableMessage>
                  </tfoot>
                ),
              }
        }
      />
    </TopbarPage>
  );
};

export default ChannelVideoContracts;
